/** @jsx jsx */
import React, { memo, useMemo } from 'react';
import { jsx } from '@compiled/react';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import {
	ContextualAnalyticsData,
	MODAL,
	FireScreenAnalytics,
	useAnalyticsEvents,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import commonMessages from '../../common/messages.tsx';
import type { ModalClosehandler } from '../../common/types.tsx';
import { descriptionMapping } from '../../common/utils.tsx';
import { BoardCreateContext } from '../../controllers/use-board-create-context/index.tsx';
import { BoardCreateForm, BoardCreateFormFallback } from './form/index.tsx';
import messages from './messages.tsx';
import type { BoardCreateModalProps } from './types.tsx';

const MODAL_WIDTH = 1000;
const MODAL_HEIGHT = 551;

export const BoardCreateModalFallback = (): JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Modal
			height={MODAL_HEIGHT}
			width={MODAL_WIDTH}
			label={formatMessage(messages.loadingLabel)}
			messageId="software-board-create.ui.modal.modal.board-create-modal-fallback"
			messageType="transactional"
		>
			<BoardCreateFormFallback />
		</Modal>
	);
};

const BoardCreateModal = ({
	boardType,
	projectId,
	onClose,
	triggerRef,
}: Omit<BoardCreateModalProps, 'projectAvatar'>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleOnClose: ModalClosehandler = (_mouseEvent, _analyticEvent, didSubmit) => {
		const event = createAnalyticsEvent({
			action: 'closed',
			actionSubject: 'modalDialog',
		});

		fireUIAnalytics(event, 'createNewBoardModal', {
			didSubmit: !!didSubmit,
			boardType: boardType.toLowerCase(),
		});
		onClose?.();
	};

	return (
		<Modal
			testId="software-board-create.ui.modal"
			onClose={handleOnClose}
			width={MODAL_WIDTH}
			height={MODAL_HEIGHT}
			messageId="software-board-create.ui.modal.modal.board-create-modal"
			messageType="transactional"
			label={`${formatMessage(commonMessages.boardCreationTitle)}. ${formatMessage(descriptionMapping[boardType])}`}
			shouldReturnFocus={triggerRef}
		>
			<BoardCreateForm projectId={projectId} boardType={boardType} onClose={handleOnClose} />
			<FireScreenAnalytics attributes={{ boardType: boardType.toLowerCase() }} />
		</Modal>
	);
};

const BoardCreateModalWithContext = ({
	props: { projectAvatar, ...restProps },
}: {
	props: BoardCreateModalProps;
}): JSX.Element => {
	const value = useMemo(
		() => ({ projectId: restProps.projectId, projectAvatar }),
		[restProps.projectId, projectAvatar],
	);

	return (
		<BoardCreateContext.Provider value={value}>
			<ShortcutScope>
				<ContextualAnalyticsData sourceName="createNewBoard" sourceType={MODAL}>
					<BoardCreateModal {...restProps} />
				</ContextualAnalyticsData>
			</ShortcutScope>
		</BoardCreateContext.Provider>
	);
};

export default memo(BoardCreateModalWithContext);
